import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { ExternalLink, Mail, Clock, CheckCircle, AlertTriangle, Loader2, GitBranch, Hash } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import type { EmailAccount } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface EmailSummary {
  uid: number;
  subject: string;
  from: any;
  date: string;
  hasAttachments: boolean;
  size?: number;
  projectId?: string;
  suggestedProjectId?: string;
  autoAssignReason?: 'thread' | 'subject_pattern' | null;
}

interface EmailDetail {
  uid: number;
  subject: string;
  from: any;
  to?: any;
  date: string;
  text?: string;
  html?: string;
  attachments?: Array<{
    filename: string;
    size: number;
    contentType?: string;
  }>;
  projectId?: string;
}

interface AccountSummaries {
  accountId: string;
  accountName: string;
  emails: EmailSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface AccountMessages {
  accountId: string;
  accountName: string;
  messages: EmailSummary[];
}

interface ProjectSuggestion {
  id: string;
  projectName: string;
  clientName: string;
}

interface UnassignedEmailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  primaryAccount: EmailAccount;
}

export function UnassignedEmailsDialog({ open, onOpenChange, primaryAccount }: UnassignedEmailsDialogProps) {
  const [selectedEmail, setSelectedEmail] = useState<{ accountId: string; uid: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [assigningEmail, setAssigningEmail] = useState<number | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [dropboxWarningDismissed, setDropboxWarningDismissed] = useState(false);
  const [showDropboxWarning, setShowDropboxWarning] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageLimit = 20;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Check Dropbox connection status when dialog opens
  const { data: dropboxStatus } = useQuery({
    queryKey: ['/api/dropbox/status'],
    enabled: open && !dropboxWarningDismissed,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/dropbox/status');
      return response.json();
    }
  });

  // Show warning if Dropbox is not connected
  useEffect(() => {
    if (dropboxStatus && !dropboxStatus.connected && !dropboxWarningDismissed) {
      setShowDropboxWarning(true);
    }
  }, [dropboxStatus, dropboxWarningDismissed]);

  // Get paginated email summaries for the primary account
  const { data: accountSummaries, isLoading: messagesLoading } = useQuery<AccountSummaries[]>({
    queryKey: ['/api/email-summaries', currentPage, pageLimit],
    enabled: open && !!primaryAccount,
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/email-summaries?page=${currentPage}&limit=${pageLimit}`);
      return response.json();
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Get assigned email UIDs
  const { data: assignedData } = useQuery<{ uid: number }[]>({
    queryKey: ['/api/email-accounts', primaryAccount.id, 'assigned-uids'],
    enabled: open && !!primaryAccount,
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/email-accounts/${primaryAccount.id}/assigned-uids`);
      return response.json();
    }
  });

  // Get recent active projects for suggestions
  const { data: recentProjects } = useQuery<ProjectSuggestion[]>({
    queryKey: ['/api/projects/recent'],
    enabled: open,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/projects/recent?limit=15');
      return response.json();
    }
  });

  // Search projects based on query
  const { data: searchResults } = useQuery<ProjectSuggestion[]>({
    queryKey: ['/api/projects/search', searchQuery],
    enabled: open && searchQuery.length >= 2,
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/projects/search?q=${encodeURIComponent(searchQuery)}`);
      return response.json();
    }
  });

  // Get email detail for selected email using the new endpoint
  const { data: emailDetail, isLoading: emailDetailLoading } = useQuery<EmailDetail | null>({
    queryKey: ['/api/email-messages', selectedEmail?.accountId, selectedEmail?.uid],
    enabled: !!selectedEmail,
    queryFn: async () => {
      if (!selectedEmail) return null;
      const response = await apiRequest('GET', `/api/email-messages/${selectedEmail.accountId}/${selectedEmail.uid}`);
      return response.json();
    },
    staleTime: 60000, // Consider email content fresh for 1 minute
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const assignToProject = async (projectId: string, uid: number) => {
    // Find email and project details for optimistic update and toast
    const emailToAssign = primaryEmails.find(e => e.uid === uid);
    const projectInfo = projectSuggestions.find(p => p.id === projectId);
    
    // Check if already assigning this email
    if (assigningEmail === uid) {
      return;
    }
    
    try {
      setAssigningEmail(uid);
      
      // Optimistically update the UI - remove email from unassigned list immediately
      queryClient.setQueryData<AccountSummaries[]>(['/api/email-summaries', currentPage, pageLimit], (old) => {
        if (!old) return old;
        return old.map(account => {
          if (account.accountId === primaryAccount.id) {
            return {
              ...account,
              emails: account.emails.filter(email => email.uid !== uid),
              total: Math.max(0, account.total - 1)
            };
          }
          return account;
        });
      });
      
      // Optimistically update assigned UIDs
      queryClient.setQueryData<{ uid: number }[]>(
        ['/api/email-accounts', primaryAccount.id, 'assigned-uids'],
        (old) => [...(old || []), { uid }]
      );
      
      // Optimistically update unassigned count
      queryClient.setQueryData<number>(
        ['/api/email-accounts', primaryAccount.id, 'unassigned-count'],
        (old) => Math.max(0, (old || 0) - 1)
      );

      const response = await apiRequest('POST', `/api/email-accounts/${primaryAccount.id}/assign`, {
        uid,
        projectId,
        assignedBy: 'user'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Assignment failed');
      }

      const result = await response.json();
      
      // Show success toast with project name if available
      toast({
        title: "✅ Email Assigned Successfully",
        description: projectInfo 
          ? `Email assigned to project ${projectId} - ${projectInfo.projectName}` 
          : `Email assigned to project ${projectId}`,
      });

      // Smart cache invalidation after success - only update what's needed
      // Invalidate the specific project's pulses without refetching all emails
      await queryClient.invalidateQueries({ 
        queryKey: ['/api/projects', projectId],
        exact: false 
      });
      
      // Invalidate project pulses to show the new pulse
      await queryClient.invalidateQueries({ 
        queryKey: ['/api/pulses'],
        exact: false
      });

      // Clear selections after successful assignment
      setSelectedProjectId(null);
      
      // If this was the selected email for viewing, clear it
      if (selectedEmail?.uid === uid) {
        setSelectedEmail(null);
      }

    } catch (error) {
      // Revert optimistic updates on error
      await queryClient.invalidateQueries({ queryKey: ['/api/email-summaries', currentPage, pageLimit] });
      await queryClient.invalidateQueries({ queryKey: ['/api/email-accounts', primaryAccount.id, 'assigned-uids'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/email-accounts', primaryAccount.id, 'unassigned-count'] });
      
      // Determine error message
      const errorMessage = error instanceof Error ? error.message : "Failed to assign email";
      const isDuplicate = errorMessage.toLowerCase().includes('already') || errorMessage.toLowerCase().includes('duplicate');
      
      toast({
        title: "❌ Assignment Failed",
        description: isDuplicate 
          ? `This email is already assigned to a project.`
          : errorMessage === "Assignment failed" 
          ? "Could not assign email to project. Please check your connection and try again."
          : errorMessage,
        variant: "destructive",
      });
      console.error('Error assigning email:', error);
    } finally {
      setAssigningEmail(null);
    }
  };

  if (!primaryAccount) return null;

  const primaryAccountData = accountSummaries?.find(am => am.accountId === primaryAccount.id);
  const primaryEmails = primaryAccountData?.emails || [];
  const assignedUIDs = new Set(assignedData?.map(item => item.uid) || []);
  const unassignedEmails = primaryEmails.filter(email => !assignedUIDs.has(email.uid));
  const totalEmails = primaryAccountData?.total || 0;
  const hasMoreEmails = primaryAccountData?.hasMore || false;
  
  // Determine which projects to show (search results or recent projects)
  const projectSuggestions = searchQuery.length >= 2 ? (searchResults || []) : (recentProjects || []);

  return (
    <>
      {/* Dropbox Warning Dialog */}
      <AlertDialog open={showDropboxWarning} onOpenChange={setShowDropboxWarning}>
        <AlertDialogContent className="max-w-2xl bg-gray-900 border-red-500 border-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-500 text-2xl flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />
              Dropbox Connection Interrupted
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-200 text-lg space-y-4">
              <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
                <p className="font-semibold text-red-400 mb-2">WARNING: Email attachments will NOT be saved!</p>
                <p>The Dropbox connection is currently interrupted. If you assign emails to projects, any attachments (PDFs, images, documents) will be lost and cannot be recovered later.</p>
              </div>
              <div className="space-y-2">
                <p className="font-medium">To fix this issue:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Go to Settings → Dropbox</li>
                  <li>Check the Dropbox access token</li>
                  <li>Generate a new token if needed</li>
                </ol>
              </div>
              <p className="text-yellow-400 font-medium">
                Only proceed if you understand that attachments will be permanently lost.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDropboxWarning(false);
                onOpenChange(false);
              }}
              className="border-gray-600 hover:bg-gray-800"
            >
              Cancel and Fix Dropbox
            </Button>
            <AlertDialogAction
              onClick={() => {
                setDropboxWarningDismissed(true);
                setShowDropboxWarning(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              I Understand - Continue Without Attachments
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] h-[90vh] bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-100 flex items-center space-x-2">
            <Mail className="w-5 h-5 text-orange-400" />
            <span>Unassigned Emails - {primaryAccount.name}</span>
            <span className="text-orange-400">({unassignedEmails.length})</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Assign incoming emails to projects to create corresponding pulses
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col h-full overflow-hidden">
          {/* Fixed Header Section */}
          <div className="flex-shrink-0 space-y-4 overflow-hidden">
            {messagesLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="text-gray-400">Loading unassigned emails...</div>
              </div>
            ) : unassignedEmails.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <Mail className="w-12 h-12 text-green-400 mb-3" />
                <div className="text-green-400 text-lg font-medium">All Emails Assigned!</div>
                <div className="text-gray-400 text-sm mt-1">No emails require project assignment</div>
              </div>
            ) : (
              <>
                {/* Project Assignment Section - Fixed Header */}
                <div className="border-b border-gray-600 pb-4">
                  <div className="flex items-center space-x-4 mb-4">
                    <h3 className="text-lg font-medium text-gray-200">Assign to Project</h3>
                  </div>
                  
                  {/* Search Field */}
                  <div className="mb-4">
                    <SearchInput
                      placeholder="Search projects by ID or name..."
                      value={searchQuery}
                      onChange={setSearchQuery}
                      className="bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-400"
                    />
                  </div>

                  {/* Project Suggestions */}
                  <div className="space-y-2">
                    <div className="h-24 overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {projectSuggestions.slice(0, 15).map((project) => (
                          <Button
                            key={project.id}
                            variant={selectedProjectId === project.id ? "default" : "outline"}
                            size="sm"
                            className={selectedProjectId === project.id 
                              ? "bg-blue-600 text-white hover:bg-blue-700 text-xs" 
                              : "border-blue-500 text-blue-400 hover:bg-blue-500/20 text-xs"
                            }
                            onClick={() => {
                              if (selectedProjectId === project.id) {
                                setSelectedProjectId(null);
                              } else {
                                setSelectedProjectId(project.id);
                              }
                            }}
                            disabled={assigningEmail !== null}
                          >
                            {project.id} - {project.projectName}
                          </Button>
                        ))}
                      </div>
                      {projectSuggestions.length === 0 && searchQuery.length >= 2 && (
                        <div className="text-sm text-gray-500 italic">No projects found matching your search.</div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Scrollable Content Section */}
          {unassignedEmails.length > 0 && (
            <div className="flex-1 overflow-y-auto min-h-0 max-h-full">
              {/* Email List Section */}
              <div className="space-y-2 pb-4 pr-2">
                <div className="text-sm text-gray-400 mb-2">
                  {selectedProjectId 
                    ? `Selected: ${selectedProjectId} - Click email buttons to assign, or click project again to deselect`
                    : "Select a project above, then click email buttons to assign. Or click email buttons to view details."
                  }
                </div>
                
                {unassignedEmails.map((message) => {
                  const messageDate = new Date(message.date);
                  const isRecent = Date.now() - messageDate.getTime() < 24 * 60 * 60 * 1000; // 24 hours
                  const isSelectedEmail = selectedEmail?.uid === message.uid;
                  const hasSuggestion = !!message.suggestedProjectId;
                  const isThreadMatch = message.autoAssignReason === 'thread';
                  const isPatternMatch = message.autoAssignReason === 'subject_pattern';
                  
                  return (
                    <div key={message.uid} className="space-y-3">
                      {/* Email Row */}
                      <div
                        className="flex items-center justify-between border border-gray-600 rounded-lg px-4 py-2 bg-gray-800/30 hover:bg-gray-800/50 transition-colors cursor-pointer group"
                        data-testid={`email-row-${message.uid}`}
                      >
                        {/* Left side: Date/Time and Subject */}
                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                          <div className="flex flex-col justify-center flex-shrink-0 w-40">
                            <div className={`text-sm font-medium ${isRecent ? 'text-blue-400' : 'text-gray-400'} leading-tight`}>
                              {format(messageDate, 'MMM dd, yyyy HH:mm')}
                            </div>
                            {isRecent && (
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3 text-blue-400" />
                                <span className="text-xs text-blue-400 leading-tight">
                                  {formatDistanceToNow(messageDate, { addSuffix: true })}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm text-gray-200 truncate leading-tight">
                                {message.subject || '(No Subject)'}
                              </div>
                              {/* Visual indicators for suggested assignments */}
                              {hasSuggestion && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div
                                        className={`flex items-center space-x-1 px-2 py-0.5 rounded text-xs font-medium ${
                                          isThreadMatch 
                                            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                                            : 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                                        }`}
                                        data-testid={`suggestion-badge-${message.uid}`}
                                      >
                                        {isThreadMatch ? (
                                          <GitBranch className="w-3 h-3" />
                                        ) : (
                                          <Hash className="w-3 h-3" />
                                        )}
                                        <span>{message.suggestedProjectId}</span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-gray-800 border-gray-600 text-white">
                                      {isThreadMatch ? (
                                        <p>Thread detected - High confidence match</p>
                                      ) : (
                                        <p>Pattern match - Subject matches project pattern</p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 font-mono truncate leading-tight">
                              {message.from?.value?.[0]?.address || message.from?.text || 'Unknown sender'}
                            </div>
                          </div>
                        </div>

                        {/* Right side: Action buttons */}
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          {/* One-click assignment button for suggested projects */}
                          {hasSuggestion && !selectedProjectId && (
                            <Button
                              size="sm"
                              variant={isThreadMatch ? "default" : "outline"}
                              className={
                                isThreadMatch 
                                  ? "bg-green-600 hover:bg-green-700 text-white" 
                                  : "border-blue-500 text-blue-400 hover:bg-blue-500/20"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                assignToProject(message.suggestedProjectId!, message.uid);
                              }}
                              disabled={assigningEmail === message.uid}
                              data-testid={`assign-suggested-${message.uid}`}
                            >
                              {assigningEmail === message.uid ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                  <span className="text-xs">Assigning...</span>
                                </>
                              ) : (
                                <>
                                  {isThreadMatch && <GitBranch className="w-3 h-3 mr-1" />}
                                  {isPatternMatch && <Hash className="w-3 h-3 mr-1" />}
                                  <span className="text-xs">Assign to {message.suggestedProjectId}</span>
                                </>
                              )}
                            </Button>
                          )}
                          
                          {/* Manual assignment or view button */}
                          <Button
                            size="sm"
                            variant="outline" 
                            className={selectedProjectId 
                              ? "border-blue-500 text-blue-400 hover:bg-blue-500/20" 
                              : "border-green-500 text-green-400 hover:bg-green-500/20"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectedProjectId) {
                                assignToProject(selectedProjectId, message.uid);
                              } else {
                                if (isSelectedEmail) {
                                  setSelectedEmail(null);
                                } else {
                                  setSelectedEmail({ accountId: primaryAccount.id, uid: message.uid });
                                }
                              }
                            }}
                            disabled={assigningEmail === message.uid}
                            data-testid={`action-button-${message.uid}`}
                          >
                            {assigningEmail === message.uid ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                <span className="text-xs">Assigning...</span>
                              </>
                            ) : selectedProjectId ? (
                              selectedProjectId
                            ) : isSelectedEmail ? (
                              "Close"
                            ) : (
                              "View"
                            )}
                          </Button>
                          <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-blue-400 transition-colors" />
                        </div>
                      </div>

                      {/* Inline Email Content */}
                      {isSelectedEmail && (
                        <div className="ml-4 border-l-2 border-blue-500/30 pl-3 mt-1 max-w-full overflow-hidden">
                          {emailDetailLoading ? (
                            <div className="flex items-center justify-center p-2">
                              <div className="text-gray-400 text-sm">Loading email content...</div>
                            </div>
                          ) : emailDetail ? (
                            <div className="bg-gray-800/20 rounded-lg p-2 w-full overflow-hidden">
                              {/* Email Content - Direct Display */}
                              <div className="h-40 w-full overflow-hidden border border-gray-600 rounded bg-white">
                                {emailDetail.html ? (
                                  <iframe
                                    srcDoc={emailDetail.html}
                                    className="w-full h-full border-0"
                                    sandbox="allow-same-origin allow-scripts"
                                    title="Email Content"
                                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                                  />
                                ) : emailDetail.text ? (
                                  <div className="h-full overflow-y-auto bg-gray-900 p-2">
                                    <pre className="text-xs text-gray-200 whitespace-pre-wrap font-mono">
                                      {emailDetail.text}
                                    </pre>
                                  </div>
                                ) : (
                                  <div className="text-gray-400 italic p-3 text-xs">No content available</div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-red-400 p-2 text-sm">Failed to load email content</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Pagination Controls */}
          {unassignedEmails.length > 0 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                Page {currentPage} - Showing {unassignedEmails.length} of {totalEmails} emails
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || messagesLoading}
                  className="border-gray-600 hover:bg-gray-800"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!hasMoreEmails || messagesLoading}
                  className="border-gray-600 hover:bg-gray-800"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}